import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";

const FIRED_KEY = "uneksia_confetti_fired_milestones";

/**
 * Fire a celebratory confetti burst the FIRST time the user lands on the
 * dashboard after hitting a streak milestone (7, 14, 30, 50, 100, 365).
 *
 * State is tracked in localStorage per user id + milestone so each
 * milestone only celebrates once per device.
 */
export default function StreakConfetti({ streak, userId }) {
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current || !streak || !userId) return;

    const current = streak.current_streak || 0;
    // Milestone every 7 days (7, 14, 21, 28, ...)
    const isMilestone = current > 0 && current % 7 === 0;
    if (!isMilestone) return;
    const hit = [current];

    // Only celebrate if we haven't fired for this exact milestone yet
    try {
      const raw = localStorage.getItem(FIRED_KEY);
      const fired = raw ? JSON.parse(raw) : {};
      const userFired = fired[userId] || [];
      const newHits = hit.filter((m) => !userFired.includes(m));
      if (newHits.length === 0) return;

      firedRef.current = true;

      // Staggered confetti: center burst + side cannons
      const duration = 2500;
      const end = Date.now() + duration;
      const colors = ["#a855f7", "#f59e0b", "#ec4899", "#10b981", "#3b82f6"];

      // Initial big burst from center
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.5 },
        colors,
      });

      // Side cannons
      const sideBurst = () => {
        confetti({
          particleCount: 40,
          angle: 60,
          spread: 55,
          origin: { x: 0, y: 0.7 },
          colors,
        });
        confetti({
          particleCount: 40,
          angle: 120,
          spread: 55,
          origin: { x: 1, y: 0.7 },
          colors,
        });
        if (Date.now() < end) requestAnimationFrame(sideBurst);
      };
      setTimeout(sideBurst, 400);

      // Persist so we don't replay
      fired[userId] = [...userFired, ...newHits];
      localStorage.setItem(FIRED_KEY, JSON.stringify(fired));
    } catch (e) {
      // noop
    }
  }, [streak, userId]);

  return null;
}
